<template>
  <div>{{ formattedTime }}</div>
</template>

<script setup lang="ts">
const props = defineProps<{
  target: Date | number | string;
  direction?: 'backward' | 'forward';
}>();

const { t } = useI18n();

const direction = props.direction ?? 'backward';

const elapsed = ref(0);
let timer: number | undefined;

function updateTime() {
  const base = new Date(props.target).getTime();
  const now = Date.now();

  elapsed.value =
    direction === 'backward'
      ? Math.max(0, base - now) // remaining ms
      : Math.max(0, now - base); // elapsed ms
}

onMounted(() => {
  updateTime();
  timer = window.setInterval(updateTime, 1000);
});

onUnmounted(() => {
  if (timer) clearInterval(timer);
});

// Format HH:MM:SS
const formattedTime = computed(() => {
  const totalSeconds = Math.floor(elapsed.value / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hoursTotal = Math.floor(totalSeconds / 3600);
  const hours = days > 0 ? hoursTotal % 24 : hoursTotal;
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pad = (n: number) => String(n).padStart(2, '0');

  const d = t('coreDaysPostfix');
  const h = t('coreHoursPostfix');
  const m = t('coreMinPostfix');
  const s = t('coreSecPostfix');

  // 1) if there's at least 1 day → show days + hours
  if (days > 0) {
    return `${days}${d} ${pad(hours)}${h}`;
  }

  // 2) if no days, but hours exist → show hours + minutes
  if (hours > 0) {
    return `${hours}${h} ${pad(minutes)}${m}`;
  }

  // 3) if no hours, but minutes exist → show minutes + seconds
  if (minutes > 0) {
    return `${minutes}${m} ${pad(seconds)}${s}`;
  }

  // 4) only seconds left
  return `${seconds}${s}`;
});
</script>
